import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Settings, Check, X } from 'lucide-react';
import { getConsent, saveConsent } from '../utils/consent';

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setAnalytics(!!existing.analytics);
      setAdvertising(!!existing.advertising);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, advertising: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false, advertising: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics, advertising });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 animate-fade-in pointer-events-none"
      data-testid="consent-banner"
    >
      <div className="max-w-2xl mx-auto bg-dark text-white rounded-2xl shadow-2xl p-5 sm:p-6 pointer-events-auto">
        {!showCustomise ? (
          <>
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-heading font-bold text-base sm:text-lg leading-tight mb-1">
                  We respect your privacy
                </p>
                <p className="text-sm text-white/85 leading-relaxed">
                  We use essential cookies to keep MDD running. With your permission, we'd also like to use:
                  <span className="block mt-2"><strong>Analytics cookies</strong> — Google Analytics so we can improve the app.</span>
                  <span className="block"><strong>Advertising cookies</strong> — Meta Pixel so we can show relevant ads to people like you.</span>
                  <span className="block mt-2 text-xs text-white/70">You can change these any time. Read our <Link to="/cookies" className="text-primary-light underline">Cookie Policy</Link>.</span>
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <button
                onClick={handleRejectAll}
                data-testid="consent-reject-btn"
                className="px-4 py-2.5 rounded-xl border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowCustomise(true)}
                data-testid="consent-customise-btn"
                className="px-4 py-2.5 rounded-xl border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5"
              >
                <Settings className="w-4 h-4" /> Customise
              </button>
              <button
                onClick={handleAcceptAll}
                data-testid="consent-accept-all-btn"
                className="px-4 py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-white text-sm font-semibold transition-colors"
              >
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="font-heading font-bold text-base sm:text-lg leading-tight">
                  Customise your cookie choices
                </p>
              </div>
              <button
                onClick={() => setShowCustomise(false)}
                aria-label="Back"
                className="p-1 text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              {/* Essential */}
              <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-sm">Essential cookies</p>
                  <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full text-white/70">Always On</span>
                </div>
                <p className="text-xs text-white/70 mt-1 leading-relaxed">
                  Required to keep you signed in and the app working. Cannot be disabled.
                </p>
              </div>

              {/* Analytics */}
              <label className="flex gap-3 rounded-xl bg-white/5 border border-white/10 p-3 cursor-pointer hover:bg-white/10 transition-colors">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  data-testid="consent-toggle-analytics"
                  className="mt-1 w-4 h-4 accent-primary"
                />
                <div className="flex-1">
                  <p className="font-semibold text-sm">Analytics cookies</p>
                  <p className="text-xs text-white/70 mt-1 leading-relaxed">
                    Google Analytics. Helps us see which pages and features people use most so we can improve them.
                  </p>
                </div>
              </label>

              {/* Advertising */}
              <label className="flex gap-3 rounded-xl bg-white/5 border border-white/10 p-3 cursor-pointer hover:bg-white/10 transition-colors">
                <input
                  type="checkbox"
                  checked={advertising}
                  onChange={(e) => setAdvertising(e.target.checked)}
                  data-testid="consent-toggle-advertising"
                  className="mt-1 w-4 h-4 accent-primary"
                />
                <div className="flex-1">
                  <p className="font-semibold text-sm">Advertising cookies</p>
                  <p className="text-xs text-white/70 mt-1 leading-relaxed">
                    Meta Pixel (Facebook & Instagram). Used to show relevant ads and measure their performance.
                  </p>
                </div>
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2.5 rounded-xl border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                data-testid="consent-save-prefs-btn"
                className="px-4 py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
